import { useState, useCallback, useMemo } from "react";
import FileSaver from "file-saver";
import { JSZipMetadata } from "jszip";
import { toast } from "react-toastify";
import {
  FormDataConverter,
  FormSchema,
  isFileInputElement,
} from "@graffer-inc/form-schema";
import apiClient from "../../../../../application/apis/api-client";
import {
  SmartApplyApiError,
  reportUnexpectedError,
  namePdfOutputFileWithLabel,
  FileType,
  FolderType,
  generateZip,
} from "@graffer-inc/smart-apply";

import { ApplicationEntity } from "../../../../../application/domain/application/application-entity";
import {
  BulkDLFilesError,
  BulkDLFilesResult,
  BulkDLFilesResultStatus,
  BulkDLFilesTarget,
  BulkDLFilesTargetLabel,
} from "../../../../../application/domain/application/bulk-download-files";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { formatToSimpleTimestamp } from "../../../../../utils/date-utils";
import { PdfRenderError } from "../../../../../application/domain/pdf-error";
import { ProcedureDetailType } from "@graffer-inc/cloud-gov-core";
import { CheckboxProps } from "@graffer-inc/console-design-kit";
import { fetchAttachedFileWithRetry } from "../../../../../application/apis/files/base";

export const useBulkDownloadFiles = (
  procedure: ProcedureEntity,
  applications: ApplicationEntity[],
): {
  targets: BulkDLFilesTarget[];
  message: string;
  options: CheckboxProps["options"];
  onToggleTarget: (target: BulkDLFilesTarget) => void;
  canDLAttached: boolean;
  canDLPdf: boolean;
  result: BulkDLFilesResult;
  onExecute: () => Promise<void>;
} => {
  const procDetail = procedure.getLiveDetail();
  const convertToErrorMessage = convertToErrorMessageFunc(procDetail);
  const canDLAttached = applications.some((a) => a.files.length > 0);
  const canDLPdf = procedure.canOutputPdf();
  const [status, setStatus] = useState<BulkDLFilesResultStatus>("CONFIRM");
  const [fetchedCount, setFetchedCount] = useState<number>(0);
  const [zippedPercent, setZippedPercent] = useState<number>(0);
  const [errors, setErrors] = useState<BulkDLFilesError[]>([]);
  const [targets, setTargets] = useState<BulkDLFilesTarget[]>([]);
  const onToggleTarget = useCallback(
    (toggled: BulkDLFilesTarget) =>
      setTargets((oldValue) =>
        oldValue.includes(toggled)
          ? oldValue.filter((t) => t !== toggled)
          : [...oldValue, toggled],
      ),
    [],
  );

  const result = useMemo<BulkDLFilesResult>(
    () => ({
      status,
      fetchedCount,
      zippedPercent,
      errors,
    }),
    [status, fetchedCount, zippedPercent, errors],
  );

  const message = useMemo<string>(() => {
    const messages: string[] = [];
    if (canDLAttached && canDLPdf) return "";
    if (!canDLAttached) {
      messages.push("選択済みの申請に添付ファイルが含まれていません。");
    }
    if (!canDLPdf) {
      messages.push(
        "この手続きには帳票レイアウトが設定されていません。帳票レイアウトは、手続き一覧の帳票レイアウトから設定できます。",
      );
    }
    return messages.join("\n");
  }, [canDLAttached, canDLPdf]);
  const options = useMemo<CheckboxProps["options"]>(
    () =>
      [
        {
          value: "ATTACHED",
          name: BulkDLFilesTargetLabel("ATTACHED"),
        },
        {
          value: "PDF",
          name: BulkDLFilesTargetLabel("PDF"),
        },
      ]
        .filter((opt) => opt.value !== "ATTACHED" || canDLAttached)
        .filter((opt) => opt.value !== "PDF" || canDLPdf),
    [canDLAttached, canDLPdf],
  );

  const downloadAttached = useCallback(
    async (apl: ApplicationEntity, withAplId: boolean): Promise<FileType[]> => {
      const data = new FormDataConverter(
        FormSchema.fromJSON(procDetail.template.formSchema),
        apl.jsonDocument,
      ).flattenWithElement();
      const fileElements = Object.keys(data)
        .map((key) => data[key])
        .filter((elem) => isFileInputElement(elem.element));
      const files: FileType[] = [];
      for (let j = 0; j < apl.files.length; j++) {
        const file = apl.files[j];
        const uri = apl.formatFileResourcesURLWithIndex(j);
        const elem = fileElements.find((v) => v.value === file.refKey);
        if (!elem) {
          // アップロード後にファイル項目が非表示になった場合など
          continue;
        }
        const name =
          elem.index !== undefined
            ? `${elem.element.label.text}[${elem.index}]_${file.name}`
            : `${elem.element.label.text}_${file.name}`;
        try {
          const res = await fetchAttachedFileWithRetry(() =>
            apiClient.fetchAttachedFile(uri),
          );
          files.push({
            name: withAplId ? `${apl.id}_${name}` : name,
            blob: res.data,
          });
        } catch (e) {
          const apiError = new SmartApplyApiError(e);
          await apiError.resolveErrorResponse();
          setErrors((errors) => [
            ...errors,
            {
              id: apl.id,
              target: "ATTACHED",
              message: convertToErrorMessage(apiError),
            },
          ]);
        }
      }
      if (files.length === 0) {
        // 対象の申請に1件も添付ファイルがない場合はエラーメッセージを表示する。
        setErrors((errors) => [
          ...errors,
          {
            id: apl.id,
            target: "ATTACHED",
            message: "添付ファイルがありません。",
          },
        ]);
      }
      return files;
    },
    [convertToErrorMessage, procDetail.template.formSchema],
  );

  const downloadPdf = useCallback(
    async (apl: ApplicationEntity): Promise<FileType[]> => {
      const files: FileType[] = [];
      try {
        const res = await fetchAttachedFileWithRetry(() =>
          apiClient.outputPDF(apl.id),
        );
        files.push({
          name: namePdfOutputFileWithLabel(apl.id),
          blob: res.data,
        });
      } catch (e) {
        const apiError = new SmartApplyApiError(e);
        await apiError.resolveErrorResponse();
        setErrors((errors) => [
          ...errors,
          {
            id: apl.id,
            target: "PDF",
            message: convertToErrorMessage(apiError),
          },
        ]);
      }
      return files;
    },
    [convertToErrorMessage],
  );

  const onExecute = useCallback(async () => {
    if (targets.includes("ATTACHED") && !canDLAttached) {
      toast.error("ダウンロード対象の申請に添付ファイルが含まれていません。");
      return;
    }
    if (targets.includes("PDF") && !canDLPdf) {
      toast.error(
        "ダウンロード対象の手続きに帳票レイアウトが設定されていません。",
      );
      return;
    }

    setStatus("FETCHING");
    const aplFolders: (FileType | FolderType)[] = [];
    for (let i = 0; i < applications.length; i++) {
      const apl = applications[i];

      const aplFolder: FileType[] = [];
      if (targets.includes("ATTACHED")) {
        const files = await downloadAttached(apl, true);
        aplFolder.push(...files);
      }
      if (targets.includes("PDF")) {
        const files = await downloadPdf(apl);
        aplFolder.push(...files);
      }
      aplFolders.push({ name: apl.id, items: aplFolder });
      setFetchedCount(i + 1);
    }
    if (aplFolders.every((a) => !("items" in a) || a.items.length === 0)) {
      // ダウンロード対象の申請に添付ファイルが1件もない場合は処理を終了する。
      // 本来、申請情報に含まれない添付ファイルはapplications.filesに保存しないことが望ましいが、
      // 空のzipがダウンロードされないよう暫定対応をしている。
      setStatus("DONE");
      return;
    }

    setStatus("ZIPPING");
    const folderName = `${procedure.id}_${formatToSimpleTimestamp(new Date())}`;
    const zip = await generateZip(
      [
        {
          name: folderName,
          items: aplFolders,
        },
      ],
      {
        encodeShiftJis: true,
        onUpdate: (metadata: JSZipMetadata) =>
          setZippedPercent(metadata.percent),
      },
    );
    FileSaver.saveAs(zip, folderName);

    setStatus("DONE");
  }, [
    targets,
    canDLAttached,
    canDLPdf,
    procedure.id,
    applications,
    downloadAttached,
    downloadPdf,
  ]);

  return {
    targets,
    onToggleTarget,
    canDLAttached,
    canDLPdf,
    result,
    onExecute,
    message,
    options,
  };
};

const convertToErrorMessageFunc = (
  procDetail: ProcedureDetailType,
): ((e: SmartApplyApiError) => string) => {
  return (e: SmartApplyApiError) => {
    if (SmartApplyApiError.isRetryable(e)) {
      return "ネットワークエラーが発生しました。時間をおいてもう一度お試しください。";
    }
    if (
      SmartApplyApiError.isErrorCode(e, "applications.pdf_field_render_failure")
    ) {
      const errorMsg = new PdfRenderError(e).message(procDetail);
      if (errorMsg) {
        return errorMsg;
      }
    }
    reportUnexpectedError(e);
    return "ファイルのダウンロード中に予期しないエラーが発生しました。お手数ですが管理者にお問い合わせください。";
  };
};
